<template lang="pug">
.liquidation-inconsistencies
  .filters-bar
    el-select(v-model="filterYear" size="small" @change="load")
      el-option(v-for="y in yearOptions" :key="y" :label="y" :value="y")
    el-select(v-model="filterMonth" size="small" clearable placeholder="Todos los meses" @change="load")
      el-option(v-for="m in monthOptions" :key="m.value" :label="m.label" :value="m.value")
    el-select(v-model="filterOrigin" size="small" clearable placeholder="Todos los orígenes" @change="load")
      el-option(label="Manual" value="manual")
      el-option(label="Automático" value="automatic")

  spinner(v-if="isLoading")

  .empty-state(v-else-if="!batches.length")
    p Sin inconsistencias pendientes

  el-table(v-else :data="batches" style="width:100%" @row-click="openPanel")
    el-table-column(label="Lote" width="160")
      template(slot-scope="s")
        span.lote-code LOTE-{{ s.row.createdAt | year }}-{{ String(s.row._id).slice(-6).toUpperCase() }}
    el-table-column(label="Prestador")
      template(slot-scope="s")
        span {{ providerName(s.row.providerSnapshot) }}
    el-table-column(label="Período" width="100")
      template(slot-scope="s")
        span {{ s.row.billingPeriod.month }}/{{ s.row.billingPeriod.year }}
    el-table-column(label="TRX" width="60" align="center")
      template(slot-scope="s")
        strong {{ s.row.items.length }}
    el-table-column(label="Esperado" width="110" align="right")
      template(slot-scope="s")
        span {{ s.row.expectedTotal | fmtMoney }}
    el-table-column(label="Facturado" width="110" align="right")
      template(slot-scope="s")
        span.amount-bad {{ s.row.invoicedTotal | fmtMoney }}
    el-table-column(label="Diferencia" width="110" align="right")
      template(slot-scope="s")
        span(:style="{ color: s.row.amountDifference > 0 ? '#E24B4A' : '#BA7517', fontWeight: 600 }")
          | {{ s.row.amountDifference > 0 ? '+' : '' }}{{ s.row.amountDifference | fmtMoney }}
    el-table-column(label="Origen" width="100")
      template(slot-scope="s")
        el-tag(size="mini" :type="s.row.origin === 'automatic' ? 'primary' : 'info'")
          | {{ s.row.origin === 'automatic' ? 'Automático' : 'Manual' }}

  //- Panel de detalle lateral
  el-drawer(
    :visible.sync="panelVisible"
    direction="rtl"
    size="520px"
    :with-header="false"
  )
    .detail-panel(v-if="selected")
      .detail-header
        .detail-title {{ selectedLoteCode }}
        el-button(type="text" @click="panelVisible = false") ✕
      .detail-sub {{ providerName(selected.providerSnapshot) }} · {{ selected.billingPeriod.month }}/{{ selected.billingPeriod.year }}

      .alert-inconsistency
        .alert-row
          strong Diferencia fuera de tolerancia
        .alert-row
          span Esperado:
          strong {{ selected.expectedTotal | fmtMoney }}
          span &nbsp;· Facturado:
          strong {{ selected.invoicedTotal | fmtMoney }}
          span &nbsp;· Diferencia:
          strong(:style="{ color: selected.amountDifference > 0 ? '#E24B4A' : '#BA7517' }")
            | {{ selected.amountDifference > 0 ? '+' : '' }}{{ selected.amountDifference | fmtMoney }}

      .section-label Datos de la factura
      .info-grid
        .info-item
          .info-label Factura N°
          .info-val {{ selected.invoiceData && selected.invoiceData.invoiceNumber || '—' }}
        .info-item
          .info-label CUIT
          .info-val {{ selected.invoiceData && selected.invoiceData.cuit || '—' }}
        .info-item
          .info-label Total facturado
          .info-val {{ selected.invoicedTotal | fmtMoney }}
        .info-item
          .info-label Total esperado
          .info-val {{ selected.expectedTotal | fmtMoney }}

      .invoice-link(v-if="selected.invoiceFile" @click="viewInvoice")
        | Ver PDF de factura adjunta

      .section-label Transacciones incluidas ({{ selected.items && selected.items.length }})
      .trx-mini
        .trx-row(v-for="item in selected.items" :key="item._id || item")
          span.trx-code {{ item.transactionCode || '—' }}
          span.trx-name {{ item.practiceSnapshot && item.practiceSnapshot.name || '—' }}
          span.trx-amount {{ item.expectedAmount | fmtMoney }}

      .resolution-section
        .section-label Resolución
        el-input(
          v-model="resolutionNote"
          type="textarea"
          :rows="2"
          placeholder="Nota interna opcional..."
          size="small"
          style="margin-bottom:12px"
        )
        .btn-row
          el-button(type="success" size="small" :loading="isSaving" @click="resolve('approve')") Aprobar igual
          el-button(size="small" :loading="isSaving" @click="resolve('request_invoice')") Pedir nueva factura
          el-button(type="danger" size="small" plain :loading="isSaving" @click="resolve('reject')") Rechazar lote
</template>

<script>
import Spinner from "@/components/ui/Spinner";
import liquidationBatchService from "@/services/liquidation-batch";

/**
 * @component LiquidationInconsistencies
 * @description Bandeja de revisión de lotes con inconsistencias de monto.
 * Permite aprobar, rechazar o solicitar nueva factura.
 */
export default {
  name: "LiquidationInconsistencies",
  components: { Spinner },
  filters: {
    fmtMoney(v) {
      if (v === null || v === undefined) return "—";
      return (
        "$ " +
        Number(v)
          .toFixed(0)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
      );
    },
    year(v) {
      return v ? new Date(v).getFullYear() : "—";
    },
  },
  data() {
    return {
      isLoading: false,
      batches: [],
      filterYear: new Date().getFullYear(),
      filterMonth: null,
      filterOrigin: null,
      panelVisible: false,
      selected: null,
      resolutionNote: "",
      isSaving: false,
    };
  },
  computed: {
    yearOptions() {
      const y = new Date().getFullYear();
      return [y - 2, y - 1, y];
    },
    monthOptions() {
      return [
        { value: 1, label: "Enero" },
        { value: 2, label: "Febrero" },
        { value: 3, label: "Marzo" },
        { value: 4, label: "Abril" },
        { value: 5, label: "Mayo" },
        { value: 6, label: "Junio" },
        { value: 7, label: "Julio" },
        { value: 8, label: "Agosto" },
        { value: 9, label: "Septiembre" },
        { value: 10, label: "Octubre" },
        { value: 11, label: "Noviembre" },
        { value: 12, label: "Diciembre" },
      ];
    },
    selectedLoteCode() {
      if (!this.selected) return "";
      return `LOTE-${new Date(this.selected.createdAt).getFullYear()}-${String(
        this.selected._id
      )
        .slice(-6)
        .toUpperCase()}`;
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      try {
        const params = { billingYear: this.filterYear };
        if (this.filterMonth) params.billingMonth = this.filterMonth;
        if (this.filterOrigin) params.origin = this.filterOrigin;
        const data = await liquidationBatchService.getInconsistencies(params);
        this.batches = data.docs || data;
      } finally {
        this.isLoading = false;
      }
    },
    async openPanel(row) {
      try {
        this.selected = await liquidationBatchService.getById(row._id);
        this.resolutionNote = "";
        this.panelVisible = true;
      } catch (e) {
        this.$message.error("Error al cargar detalle");
      }
    },
    async viewInvoice() {
      try {
        const { url } = await liquidationBatchService.getInvoiceFileUrl(
          this.selected._id
        );
        window.open(url, "_blank");
      } catch (e) {
        this.$message.error("Error al obtener archivo");
      }
    },
    async resolve(action) {
      this.isSaving = true;
      try {
        await liquidationBatchService.resolve(
          this.selected._id,
          action,
          this.resolutionNote
        );
        const msgs = {
          approve: "Lote aprobado",
          reject: "Lote rechazado",
          request_invoice: "Se solicitó nueva factura",
        };
        this.$message.success(msgs[action]);
        this.panelVisible = false;
        this.$emit("resolved");
        this.load();
      } catch (e) {
        this.$message.error(e.response?.data?.message || "Error al resolver");
      } finally {
        this.isSaving = false;
      }
    },
    providerName(s) {
      if (!s) return "—";
      return s.lastName ? `${s.lastName}, ${s.firstName || ""}` : "—";
    },
  },
};
</script>

<style lang="scss" scoped>
.liquidation-inconsistencies {
  padding: 8px 0;
}
.filters-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}
.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7294;
}
.lote-code {
  font-weight: 700;
  color: #002d5e;
}
.amount-bad {
  color: #e24b4a;
  font-weight: 600;
}
.detail-panel {
  padding: 24px;
  height: 100%;
  overflow-y: auto;
}
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}
.detail-title {
  font-size: 16px;
  font-weight: 700;
  color: #002d5e;
}
.detail-sub {
  font-size: 12px;
  color: #6b7294;
  margin-bottom: 20px;
}
.alert-inconsistency {
  background: #fcebeb;
  border: 1px solid #f5c1c1;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 16px;
  color: #a32d2d;
  font-size: 13px;
}
.alert-row {
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
}
.section-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7294;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 10px;
  margin-top: 16px;
}
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
}
.info-item {
  background: #f8f9fc;
  border-radius: 8px;
  padding: 10px 12px;
}
.info-label {
  font-size: 11px;
  color: #6b7294;
  margin-bottom: 2px;
}
.info-val {
  font-size: 13px;
  font-weight: 600;
  color: #1a1f36;
}
.invoice-link {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #002d5e;
  font-weight: 600;
  cursor: pointer;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
}
.trx-mini {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
}
.trx-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f2f7;
  font-size: 12px;
  &:last-child {
    border-bottom: none;
  }
}
.trx-code {
  font-family: monospace;
  color: #05c49a;
  font-weight: 700;
  min-width: 90px;
}
.trx-name {
  flex: 1;
  color: #1a1f36;
}
.trx-amount {
  font-weight: 600;
  color: #1a1f36;
}
.resolution-section {
  border-top: 1px solid #e2e8f0;
  padding-top: 16px;
  margin-top: 8px;
}
.btn-row {
  display: flex;
  gap: 8px;
}
</style>
